import * as React from 'react';
import { flatten } from 'lodash';
import { connect } from 'react-redux';

import { withRouter } from 'libs/router';

import withInfluencerMeta from 'components/brands-filter/WithInfluencerMeta';
import GenericListsModal from 'components/generic-lists-modal/GenericListsModal';

import { addInfluencerToLists, removeInfluencerFromLists } from 'modules/lists/reducers/lists';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';

import { __t, applyTranslations } from 'utils/language';

import './add-to-lists-modal.scss';

import type {
  CheckboxesListItem,
  InfluencerMeta,
  InfluencerProfile,
  List
} from 'types';

applyTranslations(require('./add-to-lists-modal.i18n.json'));

type OwnProps = {
  influencerProfile: InfluencerProfile,
  influencerMeta: InfluencerMeta,
  copyFromListId?: number,
  params?: {
    listId: string
  },
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchAddInfluencerToLists: (d: Array<CheckboxesListItem>, c: number, b: number, a?: number | null) => Promise<void>,
  dispatchRemoveInfluencerFromLists: (d: Array<CheckboxesListItem>, c: number, b: number, a?: string | null) => Promise<void>,
  dispatchUpdateInfluencerMeta: (a: number) => Promise<void>
};

type Props = OwnProps & DispatchProps;

class AddToListsModal extends React.PureComponent<Props> {
  updateInfluencerMeta = () => {
    const { influencerProfile, dispatchUpdateInfluencerMeta } = this.props;
    dispatchUpdateInfluencerMeta(influencerProfile.id);
  };

  handleAdd = (item: CheckboxesListItem, list: List): void => {
    const { influencerProfile: { id }, dispatchAddInfluencerToLists, copyFromListId } = this.props;

    dispatchAddInfluencerToLists([item], list.organization.id, id, copyFromListId)
      .then(this.updateInfluencerMeta);
  };

  handleRemove = (item: CheckboxesListItem, list: List): void => {
    const {
      params,
      influencerProfile: { id },
      dispatchRemoveInfluencerFromLists
    } = this.props;

    dispatchRemoveInfluencerFromLists([item], list.organization.id, id, params && params.listId)
      .then(this.updateInfluencerMeta);
  };

  handleListItemClick = (item: CheckboxesListItem, list: List): void => {
    if (item.isDisabled || !list) {
      return;
    }

    if (!item.isSelected) {
      this.handleAdd(item, list);
    } else {
      this.handleRemove(item, list);
    }
  };

  render(): React.ReactNode {
    const { onCloseModal, influencerProfile, params, influencerMeta } = this.props;

    const selectedItems = flatten(Object.keys(influencerMeta.lists).map(key => influencerMeta.lists[key] || []));

    return (
      <GenericListsModal
        withCollectionCreate
        modalHeader={__t('add_to_lists_modal.list_title')}
        modalSubTitle={__t('add_to_lists_modal.lists_checkboxes_title')}
        params={params}
        onCloseModal={onCloseModal}
        selectedItems={selectedItems}
        influencerMeta={influencerMeta}
        influencerProfile={influencerProfile}
        onListItemClick={this.handleListItemClick}
      />
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddInfluencerToLists: (addToLists, organizationId, influencerId, copyFromListId) =>
    dispatch(addInfluencerToLists(addToLists.map(list => list.id), organizationId, influencerId, copyFromListId)),

  dispatchRemoveInfluencerFromLists: (removeFromLists, organizationId, influencerId, listId) =>
    dispatch(removeInfluencerFromLists(removeFromLists.map(list => list.id), organizationId, influencerId, listId)),

  dispatchUpdateInfluencerMeta: (influencerId) => dispatch(fetchInfluencersMeta([influencerId]))
});

const EnhancedAddToListsModal = withInfluencerMeta()(AddToListsModal);

export default connect(undefined, mapDispatchToProps)(withRouter(EnhancedAddToListsModal));
